import type { ComponentProps } from 'react';
import { RouterLinkAs } from '@/lib/router-link';
import { PersonRow } from '@leanin/ui';
import { memberPath } from '../lib/routes';
import { MemberAvatar, useMemberDisplayName, type MemberIdentity } from './MemberIdentity';

type PersonRowProps = ComponentProps<typeof PersonRow>;
type IdentityLayoutProps = Omit<
  PersonRowProps,
  'href' | 'linkAs' | 'name' | 'photo' | 'avatarSlot' | 'nameColor'
>;

type Props =
  | (Omit<PersonRowProps, 'href' | 'linkAs'> & {
      member?: never;
      // Pass `null` (or omit) to render a non-clickable row. The override
      // lets the suggested-members rail thread `?from-rec=...&iset=...` onto
      // the link without bypassing the wrapper.
      userId?: string | null;
      href?: string;
    })
  | (IdentityLayoutProps & {
      /** A member-or-deleted identity from the API. A deleted member renders
       *  as a muted "Deleted user" with the generic avatar and no link. */
      member: MemberIdentity;
      userId?: never;
      href?: never;
    });

export function MemberRow(props: Props) {
  if (props.member !== undefined) {
    const { member, userId: _userId, href: _href, ...rest } = props;
    return <IdentityRow member={member} {...rest} />;
  }
  const { userId, href, member: _member, ...rest } = props;
  const resolvedHref = href ?? (userId ? memberPath(userId) : undefined);
  if (resolvedHref) {
    return <PersonRow href={resolvedHref} linkAs={RouterLinkAs} {...rest} />;
  }
  return <PersonRow {...rest} />;
}

function IdentityRow({
  member,
  avatarSize = 36,
  ...rest
}: IdentityLayoutProps & { member: MemberIdentity }) {
  const name = useMemberDisplayName(member);
  if (member.kind === 'member') {
    return (
      <PersonRow
        name={name}
        {...(member.photo !== null ? { photo: member.photo } : {})}
        avatarSize={avatarSize}
        href={memberPath(member.id)}
        linkAs={RouterLinkAs}
        {...rest}
      />
    );
  }
  return (
    <PersonRow
      name={name}
      nameColor="mute"
      avatarSize={avatarSize}
      avatarSlot={<MemberAvatar member={member} size={avatarSize} decorative />}
      {...rest}
    />
  );
}
